import { type ReactNode } from "react";
import { AlertTriangle, CircleAlert, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export type RecordAlertTone = "warning" | "error" | "info";

export interface RecordAlert {
  id: string;
  tone: RecordAlertTone;
  /** What needs deciding, e.g. "Legacy household role needs review". */
  title: string;
  body?: ReactNode;
  /** The one thing to do about it: a link or button. */
  action?: ReactNode;
}

export interface RecordAlertsProps {
  alerts: RecordAlert[];
  className?: string;
}

const TONE: Record<RecordAlertTone, { icon: typeof Info; box: string; icon_: string }> = {
  warning: { icon: AlertTriangle, box: "border-warning/40 bg-warning/10", icon_: "text-warning" },
  error: { icon: CircleAlert, box: "border-destructive/40 bg-destructive/10", icon_: "text-destructive" },
  info: { icon: Info, box: "border-border bg-muted/40", icon_: "text-muted-foreground" },
};

/**
 * T2 alert slot: 0..n callouts under the hero, above the tabs. Renders nothing, and takes no
 * space, when there are none. `role="status"` per ACCESSIBILITY.md § T2 (`role="alert"` is
 * for blocking cases only). A live region doesn't reliably announce what's already in it on
 * load, so RecordPageTemplate also says how many need attention in its arrival announcement.
 */
export function RecordAlerts({ alerts, className }: RecordAlertsProps) {
  if (alerts.length === 0) return null;
  return (
    <div role="status" aria-label="Needs attention" className={cn("flex flex-col gap-2", className)}>
      {alerts.map((alert) => {
        const tone = TONE[alert.tone];
        const Icon = tone.icon;
        return (
          <div
            key={alert.id}
            className={cn("flex flex-col gap-3 rounded-[var(--card-radius)] border px-4 py-3 sm:flex-row sm:items-center sm:justify-between", tone.box)}
          >
            <div className="flex min-w-0 items-start gap-3">
              <Icon className={cn("mt-0.5 size-4 shrink-0", tone.icon_)} aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{alert.title}</p>
                {alert.body ? <div className="mt-0.5 text-sm text-muted-foreground">{alert.body}</div> : null}
              </div>
            </div>
            {alert.action
              ? (
                <div className="shrink-0 pl-7 sm:pl-0 [&_a]:inline-flex [&_a]:min-h-11 [&_a]:items-center [&_button]:min-h-11 lg:[&_a]:min-h-6 lg:[&_button]:min-h-6">
                  {alert.action}
                </div>
              )
              : null}
          </div>
        );
      })}
    </div>
  );
}
